import React, { useState, useEffect, useRef } from "react";
import Inputmask from "inputmask";
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Box,
  IconButton,
  Checkbox,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import HandshakeIcon from "@mui/icons-material/Handshake";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useLocation, Navigate } from "react-router-dom";
import RecifeBemLogo from "~/images/illustrations/bora-impactar.svg";
import NgoRegistrationClient from "../api/NgoRegistrationClient";
import CausesClient from "../api/CausesClient";
import RegistrationSuccess from "../../components/RegistrationSuccess";
import UpdateSuccess from "../../components/UpdateSuccess";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { APP_ROUTES } from '~/single-page/constants/routes';
import { useRailsContext } from '../contexts/RailsContext';

const MaskedTextField = ({ mask, value, onChange, ...props }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      const im = new Inputmask(mask, {
        showMaskOnHover: false,
        showMaskOnFocus: false,
        onBeforePaste: (pastedValue) => {
          return pastedValue;
        }
      });
      im.mask(inputRef.current);
    }
  }, [mask]);

  return (
    <TextField
      inputRef={inputRef}
      value={value}
      onChange={onChange}
      {...props}
    />
  );
};

const URLInput = ({ value, onChange, ...props }) => {
  const handleChange = (e) => {
    let newValue = e.target.value;
    
    newValue = newValue.replace(/https?:\/\/(https?:\/\/)+/, 'https://');
    newValue = newValue.replace(/^(https?:\/\/)\/+/, '$1');
    
    onChange({ ...e, target: { ...e.target, value: newValue, name: props.name } });
  };

  const handleBlur = (e) => {
    let newValue = e.target.value.trim();
    
    if (newValue && !newValue.match(/^https?:\/\//)) {
      newValue = `https://${newValue}`;
      onChange({ 
        ...e, 
        target: { 
          ...e.target, 
          value: newValue, 
          name: props.name 
        } 
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    let cleanUrl = pastedText.trim();
    cleanUrl = cleanUrl.replace(/https?:\/\/(https?:\/\/)+/, 'https://');
    cleanUrl = cleanUrl.replace(/^(https?:\/\/)\/+/, '$1');
    
    onChange({ 
      ...e, 
      target: { 
        ...e.target, 
        value: cleanUrl, 
        name: props.name 
      } 
    });
  };

  return (
    <TextField
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onPaste={handlePaste}
      {...props}
    />
  );
};

export default function RegisterPage() {  
  const theme = useTheme();
  const location = useLocation();
  const { currentUser } = useRailsContext();
  const isEditMode = location.state?.editMode;
  const previousData = location.state?.registrationData;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmall = useMediaQuery('(max-width:480px)');
  
  const [organizationType, setOrganizationType] = useState("ong");
  const [selectedOds, setSelectedOds] = useState([]);
  const [selectedCauses, setSelectedCauses] = useState([]);
  const [causes, setCauses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorType, setErrorType] = useState('error'); // 'error', 'warning', 'info'
  const [formErrors, setFormErrors] = useState({
    name: false,
    cnpj: false,
    email: false,
    address: false,
    city: false,
    state: false,
    cep: false,
    causes: false,
    galleryImages: false
  });
  const [loginErrors, setLoginErrors] = useState({
    name: false,
    email: false,
    password: false,
    confirmPassword: false
  });
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [galleryFiles, setGalleryFiles] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startYear: '',
    email: '',
    phone: '',
    instagram: '',
    facebook: '',
    x: '',
    website: '',
    cnpj: '',
    pix: '',
    cep: '',
    address: '',
    number: '',
    neighborhood: '',
    city: '',
    state: ''
  });
  
  const [loginData, setLoginData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  // Função utilitária para processar erros de forma inteligente
  const processError = (error) => {
    console.error('Erro capturado:', error);
    
    let message = "Ocorreu um erro inesperado. Tente novamente.";
    let type = 'error';
    
    // Se é um objeto com propriedade type (quando chamado manualmente)
    if (error?.type) {
      message = error.message || message;
      type = error.type;
    }
    // Se é uma resposta HTTP estruturada (axiosClient retorna error.response)
    else if (error?.data) {
      // Verifica o status para determinar o tipo
      const status = error.status;
      
      // Erro do backend com mensagem
      if (error.data.message) {
        // Se mensagem vier como array, junta em string
        message = Array.isArray(error.data.message) 
          ? error.data.message.join('. ') 
          : error.data.message;
        type = status === 422 ? 'warning' : 'error';
      }
      // Erro genérico do backend
      else if (error.data.error) {
        message = error.data.error;
        type = status === 404 ? 'info' : 'error';
      }
      // Erros de validação específicos (Rails format)
      else if (error.data.errors && typeof error.data.errors === 'object') {
        const fieldErrors = Object.entries(error.data.errors)
          .map(([field, errors]) => {
            const errorText = Array.isArray(errors) ? errors.join(', ') : errors;
            return field === 'base' ? errorText : `${field}: ${errorText}`;
          })
          .join('. ');
        message = fieldErrors.includes(':') ? `Corrija os seguintes campos: ${fieldErrors}` : fieldErrors;
        type = 'warning';
      }
    }
    // Se é um erro de string simples (Network errors, etc)
    else if (error?.message) {
      message = error.message;
      
      // Detecta tipos específicos de erro
      if (error.message.includes('Network Error') || error.message.includes('conexão')) {
        message = "Verifique sua conexão com a internet e tente novamente.";
        type = 'error';
      }
    }
    // Se é string direta
    else if (typeof error === 'string') {
      message = error;
      type = 'info';
    }
    
    // Contextualizações adicionais baseadas no conteúdo da mensagem
    if (message.toLowerCase().includes('validação') || 
        message.toLowerCase().includes('obrigatório') ||
        message.toLowerCase().includes('corrija os campos')) {
      type = 'warning';
    }
    
    if (message.toLowerCase().includes('rede') || 
        message.toLowerCase().includes('conexão') ||
        message.toLowerCase().includes('servidor')) {
      type = 'error';
    }
    
    if (message.toLowerCase().includes('sucesso')) {
      type = 'success';
    }

    // Marcar campos específicos para erros relacionados
    if (message.toLowerCase().includes('usuário já vinculado') || 
        message.toLowerCase().includes('email') ||
        message.toLowerCase().includes('já existe uma solicitação')) {
      // Marca o campo de email como erro já que é relacionado ao usuário
      setLoginErrors(prev => ({ ...prev, email: "Relacionado ao erro abaixo" }));
    }
    
    setErrorMessage(message);
    setErrorType(type);
  };

  const clearError = () => {
    setErrorMessage(null);
    setErrorType('error');
  };

  // Carregar causas disponíveis
  useEffect(() => {
    const loadCauses = async () => {
      try {
        const causesData = await CausesClient.index();
        setCauses(causesData || []);
      } catch (error) {
        console.error('Erro ao carregar causas:', error);
        processError({
          message: 'Erro ao carregar as causas disponíveis. Algumas funcionalidades podem estar limitadas.',
          type: 'warning'
        });
      }
    };
    
    loadCauses();
  }, []);

  if (currentUser) {
    if (!isEditMode || !previousData?.status) {
      return <Navigate to={APP_ROUTES.ROOT} replace />;
    }
    
    if (previousData.status !== 'rejected') {
      return <Navigate to={APP_ROUTES.ROOT} replace />;
    }
  }

  useEffect(() => {
    if (isEditMode && previousData) {
      const { ngo, user } = previousData;
      
      if (ngo) {
        setOrganizationType(ngo.type || "ong");
        setSelectedOds(ngo.ods || []);
        setSelectedCauses(ngo.causes || []);
        
        const newFormData = {
          name: ngo.name || '',
          description: ngo.description || '',
          startYear: ngo.start_year || '',
          email: ngo.email || '',
          phone: ngo.phone || '',
          instagram: ngo.instagram || '',
          facebook: ngo.facebook || '',
          x: ngo.x || '',
          website: ngo.site || '',
          cnpj: ngo.cnpj || '',
          pix: ngo.pix || '',
          cep: ngo.cep || '',
          address: ngo.address || '',
          number: ngo.number || '',
          neighborhood: ngo.neighborhood || '',
          city: ngo.city || '',
          state: ngo.state || ''
        };

        setFormData(newFormData);
      }

      if (user) {
        const newLoginData = {
          name: user.user_name || '',
          email: user.user_email || '',
          password: '',
          confirmPassword: ''
        };

        setLoginData(newLoginData);
      }
    }
  }, [isEditMode, previousData]);
  
  const handleOdsSelection = (odsNumber) => {
    if (selectedOds.includes(odsNumber)) {
      setSelectedOds(selectedOds.filter(ods => ods !== odsNumber));
    } else {
      setSelectedOds([...selectedOds, odsNumber]);
    }
  };

  const handleCausesChange = (event) => {
    const value = event.target.value;
    setSelectedCauses(typeof value === 'string' ? value.split(',') : value);
    
    // Limpar erro de causas se algo foi selecionado
    if ((typeof value === 'string' ? value.split(',') : value).length > 0) {
      setFormErrors(prev => ({ ...prev, causes: false }));
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Para campos de email, converter para minúsculas
    let processedValue = value;
    if (name === 'email') {
      processedValue = value.toLowerCase();
    }
    
    setFormData({
      ...formData,
      [name]: processedValue
    });
    
    // Limpa erro quando usuário digita
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
    
    // Limpa erro geral se usuário está corrigindo algo
    if (errorMessage && errorType === 'warning') {
      clearError();
    }
  };
  
  const handleLoginDataChange = (e) => {
    const { name, value } = e.target;
    
    // Para campos de email, converter para minúsculas
    let processedValue = value;
    if (name === 'email') {
      processedValue = value.toLowerCase();
    }
    
    setLoginData({
      ...loginData,
      [name]: processedValue
    });
    
    if (loginErrors[name]) {
      setLoginErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
    
    // Limpa erro geral se usuário está corrigindo algo
    if (errorMessage && errorType === 'warning') {
      clearError();
    }
  };
  
  const validateImageFile = (file) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const allowedExtensions = ['.png', '.jpeg', '.jpg', '.PNG', '.JPEG', '.JPG'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    // Verificar por tipo MIME
    if (!allowedTypes.includes(file.type)) {
      // Verificar por extensão do arquivo como fallback
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExtension) {
        return {
          valid: false,
          message: 'Formato de arquivo não suportado. Use apenas PNG, JPEG ou JPG.'
        };
      }
    }
    
    if (file.size > maxSize) {
      return {
        valid: false,
        message: 'Arquivo muito grande. Tamanho máximo permitido: 5MB.'
      };
    }
    
    return { valid: true };
  };

  const handleLogoUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validation = validateImageFile(file);
      
      if (!validation.valid) {
        processError({
          message: validation.message,
          type: 'warning'
        });
        e.target.value = ''; // Limpa o input
        return;
      }
      
      setLogoFile(file);
      clearError(); // Limpa erros anteriores se o arquivo for válido
    }
  };
  
  const handleCoverUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validation = validateImageFile(file);
      
      if (!validation.valid) {
        processError({
          message: validation.message,
          type: 'warning'
        });
        e.target.value = ''; // Limpa o input
        return;
      }
      
      setCoverFile(file);
      clearError(); // Limpa erros anteriores se o arquivo for válido
    }
  };

  const handleGalleryUpload = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const remainingSlots = 5 - galleryFiles.length;
      const filesToAdd = newFiles.slice(0, remainingSlots);
      
      // Validar cada arquivo antes de adicionar
      const validFiles = [];
      const invalidFiles = [];
      
      filesToAdd.forEach(file => {
        const validation = validateImageFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });
      
      // Se há arquivos inválidos, mostrar erro
      if (invalidFiles.length > 0) {
        const errorMessage = `Os seguintes arquivos não foram aceitos: ${invalidFiles.join(', ')}. Use apenas PNG, JPEG ou JPG.`;
        processError({
          message: errorMessage,
          type: 'warning'
        });
      }
      
      // Adicionar apenas arquivos válidos
      if (validFiles.length > 0) {
        const updatedFiles = [...galleryFiles, ...validFiles];
        setGalleryFiles(updatedFiles);
        
        // Limpar erro de galeria se tem pelo menos 4 imagens
        if (updatedFiles.length >= 4) {
          setFormErrors(prev => ({ ...prev, galleryImages: false }));
        }
        
        // Limpar erro se todos os arquivos foram válidos
        if (invalidFiles.length === 0) {
          clearError();
        }
      }
      
      // Limpar o input para permitir seleção dos mesmos arquivos novamente
      e.target.value = '';
    }
  };

  const handleRemoveGalleryImage = (index) => {
    const updatedFiles = galleryFiles.filter((_, i) => i !== index);
    setGalleryFiles(updatedFiles);
    
    // Reativar erro se ficaram menos de 4 imagens
    if (updatedFiles.length < 4) {
      setFormErrors(prev => ({ ...prev, galleryImages: true }));
    }
  };
  
  const handleRemoveLogo = () => {
    setLogoFile(null);
  };
  
  const handleRemoveCover = () => {
    setCoverFile(null);
  };

  const handleCepBlur = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            address: data.logradouro || '',
            neighborhood: data.bairro || '',
            city: data.localidade || '',
            state: data.uf || ''
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };
  
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newFormErrors = { 
      name: false, 
      cnpj: false, 
      email: false,
      address: false, 
      city: false, 
      state: false, 
      cep: false,
      causes: false,
      galleryImages: false
    };
    const newLoginErrors = { name: false, email: false, password: false, confirmPassword: false };
    let hasError = false;
    
    // Validar arquivos de imagem se existirem
    if (logoFile) {
      const logoValidation = validateImageFile(logoFile);
      if (!logoValidation.valid) {
        processError({
          message: `Logo: ${logoValidation.message}`,
          type: 'warning'
        });
        hasError = true;
      }
    }
    
    if (coverFile) {
      const coverValidation = validateImageFile(coverFile);
      if (!coverValidation.valid) {
        processError({
          message: `Foto de capa: ${coverValidation.message}`,
          type: 'warning'
        });
        hasError = true;
      }
    }
    
    if (galleryFiles.length > 0) {
      for (let i = 0; i < galleryFiles.length; i++) {
        const galleryValidation = validateImageFile(galleryFiles[i]);
        if (!galleryValidation.valid) {
          processError({
            message: `Imagem ${i + 1} da galeria: ${galleryValidation.message}`,
            type: 'warning'
          });
          hasError = true;
          break;
        }
      }
    }
    
    if (!formData.name) {
      newFormErrors.name = true;
      hasError = true;
    }
    
    if (organizationType === "ong" && !formData.cnpj) {
      newFormErrors.cnpj = true;
      hasError = true;
    }

    // Validação de endereço
    if (!formData.cep) {
      newFormErrors.cep = true;
      hasError = true;
    }

    if (!formData.address) {
      newFormErrors.address = true;
      hasError = true;
    }

    if (!formData.city) {
      newFormErrors.city = true;
      hasError = true;
    }

    if (!formData.state) {
      newFormErrors.state = true;
      hasError = true;
    }

    // Validação de causas (obrigatório)
    if (!selectedCauses || selectedCauses.length === 0) {
      newFormErrors.causes = true;
      hasError = true;
    }

    // Validação de galeria de imagens (mínimo 4 fotos)
    if (!galleryFiles || galleryFiles.length < 4) {
      newFormErrors.galleryImages = true;
      hasError = true;
    }
    
    if (!loginData.name) {
      newLoginErrors.name = true;
      hasError = true;
    }
    
    if (!loginData.email) {
      newLoginErrors.email = "E-mail é obrigatório";
      hasError = true;
    } else if (!validateEmail(loginData.email)) {
      newLoginErrors.email = "E-mail deve ter formato válido";
      hasError = true;
    }
    
    if (!isEditMode) {
      if (!loginData.password) {
        newLoginErrors.password = true;
        hasError = true;
      }
      
      if (loginData.password !== loginData.confirmPassword) {
        newLoginErrors.confirmPassword = true;
        hasError = true;
      }
    }

    if (formData.email && !validateEmail(formData.email)) {
      newFormErrors.email = "E-mail deve ter formato válido";
      hasError = true;
    }
    
    setFormErrors(newFormErrors);
    setLoginErrors(newLoginErrors);
    
    if (hasError) {
      processError({ 
        message: "Por favor, corrija os campos destacados",
        type: 'warning'
      });
      return false;
    }
    
    // Limpa erro se validação passou
    clearError();
    return true;
  };
  
  const handleSubmit = async () => {
    clearError();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const ngoData = {
        name: formData.name,
        description: formData.description,
        start_year: formData.startYear,
        email: formData.email,
        phone: formData.phone,
        instagram: formData.instagram,
        facebook: formData.facebook,
        x: formData.x,
        site: formData.website,
        cnpj: formData.cnpj,
        pix: formData.pix,
        cep: formData.cep,
        address: formData.address,
        number: formData.number,
        neighborhood: formData.neighborhood,
        city: formData.city,
        state: formData.state,
        type: organizationType,
        ods: selectedOds,
        causes: selectedCauses
      };

      let responseData;
      
      if (isEditMode) {
        responseData = await NgoRegistrationClient.updateNgo(ngoData, logoFile, coverFile, galleryFiles);
      } else {
        const userData = {
          user_name: loginData.name,
          user_email: loginData.email,
          password: loginData.password
        };
        responseData = await NgoRegistrationClient.registerNgo(ngoData, userData, logoFile, coverFile, galleryFiles);
      }
      
      if (responseData) {
        if (isEditMode) {
          setRegistrationSuccess(true);
          setShowForm(false);
          window.scrollTo(0, 0);
        } else {
          setRegistrationSuccess(true);
          setShowForm(false); 
          window.scrollTo(0, 0);
        }
      } else {
        processError("A resposta da API foi recebida, mas parece estar vazia.");
      }
    } catch (err) {
      console.error('Erro no cadastro:', err);
      processError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!showForm && registrationSuccess === true) {
    return isEditMode ? <UpdateSuccess /> : <RegistrationSuccess />;
  }

  return (
    <>
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#CBEFFF',
          position: 'relative',
          pt: { xs: 8, sm: 8, md: 10 },
          pb: { xs: 4, sm: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        <ToastContainer autoClose={8000} />
        <IconButton 
          onClick={() => {
            if (window.currentUser) {
              window.location.href = '/';
            } else {
              window.location.href = APP_ROUTES.SIGN_IN;
            }
          }}
          sx={{ 
            position: 'absolute',
            top: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 24 },
            color: theme.palette.primary.main,
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {showForm && (
          <Container 
            maxWidth="lg" 
            sx={{ 
              p: 0,
              height: '100%',
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' }
            }}
          >
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 2.5, sm: 3, md: 3.5 }, 
                borderRadius: { xs: 2, sm: 3 },
                bgcolor: 'white',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)'
                }
              }}
            >
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%'
              }}>
                <img 
                  src={RecifeBemLogo} 
                  alt="Bora Impactar" 
                  style={{
                    width: '160px',
                    maxWidth: '100%',
                    height: 'auto',
                    marginBottom: '20px'
                  }}
                />

                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    textAlign: isMobile ? 'center' : 'left',
                    fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    width: '100%',
                    mb: 3
                  }}
                >
                  {isEditMode ? "" : "Tipo de Organização"}
                </Typography>

                {!isEditMode && (
                  <RadioGroup
                    row
                    value={organizationType}
                    onChange={(e) => setOrganizationType(e.target.value)}
                    sx={{ 
                      justifyContent: isMobile ? 'center' : 'flex-start',
                      flexWrap: 'wrap',
                      width: '100%',
                      mb: 4
                    }}
                  >
                    <FormControlLabel 
                      value="ong" 
                      control={<Radio color="primary" />} 
                      label={
                        <Tooltip 
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>O que é uma ONG?</Typography>
                              <Typography variant="body2">
                                Organização Não Governamental é uma entidade privada sem fins lucrativos, 
                                legalmente constituída, com CNPJ e estatuto social. Atuam em diversas áreas 
                                como assistência social, educação, saúde, meio ambiente, etc.
                              </Typography>
                            </Box>
                          } 
                          arrow 
                          placement="top"
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', cursor: 'help' }}>
                            <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography>ONG</Typography>
                          </Box>
                        </Tooltip>
                      }
                    />
                    <FormControlLabel 
                      value="projeto" 
                      control={<Radio color="primary" />} 
                      label={
                        <Tooltip 
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>O que é um Projeto?</Typography>
                              <Typography variant="body2">
                                Um projeto social é uma iniciativa, geralmente sem CNPJ próprio, que visa 
                                alcançar um objetivo específico de impacto social. Pode ser uma ação temporária ou 
                                contínua, desenvolvida por indivíduos ou grupos, sem necessariamente ter uma 
                                estrutura jurídica estabelecida.
                              </Typography>
                            </Box>
                          } 
                          arrow 
                          placement="top"
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', cursor: 'help' }}>
                            <HandshakeIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography>Projeto</Typography>
                          </Box>
                        </Tooltip>
                      }
                    />
                  </RadioGroup>
                )}

                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    textAlign: isMobile ? 'center' : 'left',
                    fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    width: '100%',
                    mb: 3
                  }}
                >
                  {isEditMode 
                    ? `Editar ${organizationType === "ong" ? "ONG" : "Projeto"}`
                    : `Cadastrar ${organizationType === "ong" ? "ONG" : "Projeto"}`
                  }
                </Typography>

                <Grid container spacing={isMobile ? 2 : 3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nome"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="Nome da organização"
                      variant="outlined"
                      required
                      error={formErrors.name}
                      helperText={formErrors.name ? "Nome é obrigatório" : ""}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Ano de Início"
                      name="startYear"
                      value={formData.startYear}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        handleFormChange({
                          ...e,
                          target: {
                            ...e.target,
                            value,
                            name: e.target.name
                          }
                        });
                      }}
                      placeholder="Ex: 2020"
                      variant="outlined"
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 4
                      }}
                    />
                  </Grid>
                  
                  {organizationType === "ong" && (
                    <Grid item xs={12} sm={6}>
                      <MaskedTextField
                        mask="99.999.999/9999-99"
                        fullWidth
                        label="CNPJ"
                        name="cnpj"
                        value={formData.cnpj}
                        onChange={handleFormChange}
                        placeholder="00.000.000/0000-00"
                        variant="outlined"
                        required
                        error={formErrors.cnpj}
                        helperText={formErrors.cnpj ? "CNPJ é obrigatório para ONGs" : ""}
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12} sm={organizationType === "ong" ? 6 : 12}>
                    <MaskedTextField
                      mask="(99) 99999-9999"
                      fullWidth
                      label="Telefone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder="(00) 00000-0000"
                      variant="outlined"
                    />
                  </Grid>

                  {/* SEÇÃO DE ENDEREÇO */}
                  <Grid item xs={12}>
                    <Typography 
                      variant="h6" 
                      gutterBottom 
                      sx={{ 
                        mt: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                      }}
                    >
                      Endereço
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <MaskedTextField
                      mask="99999-999"
                      fullWidth
                      label="CEP"
                      name="cep"
                      value={formData.cep}
                      onChange={handleFormChange}
                      onBlur={handleCepBlur}
                      placeholder="00000-000"
                      variant="outlined"
                      required
                      error={formErrors.cep}
                      helperText={formErrors.cep ? "CEP é obrigatório" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Endereço"
                      name="address"
                      value={formData.address}
                      onChange={handleFormChange}
                      placeholder="Rua, avenida..."
                      variant="outlined"
                      required
                      error={formErrors.address}
                      helperText={formErrors.address ? "Endereço é obrigatório" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Número"
                      name="number"
                      value={formData.number}
                      onChange={handleFormChange}
                      placeholder="123"
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Bairro"
                      name="neighborhood"
                      value={formData.neighborhood}
                      onChange={handleFormChange}
                      placeholder="Nome do bairro"
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cidade"
                      name="city"
                      value={formData.city}
                      onChange={handleFormChange}
                      placeholder="Nome da cidade"
                      variant="outlined"
                      required
                      error={formErrors.city}
                      helperText={formErrors.city ? "Cidade é obrigatória" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Estado"
                      name="state"
                      value={formData.state}
                      onChange={handleFormChange}
                      placeholder="PE"
                      variant="outlined"
                      required
                      error={formErrors.state}
                      helperText={formErrors.state ? "Estado é obrigatório" : ""}
                      inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Descrição"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      placeholder="Descreva a organização, história, atividades, público atendido e impacto social..."
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <MaskedTextField
                      mask="email"
                      fullWidth
                      label="E-mail"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="contato@exemplo.org.br"
                      variant="outlined"
                      error={!!formErrors.email}
                      helperText={formErrors.email || ""}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <URLInput
                      fullWidth
                      label="Site"
                      name="website"
                      value={formData.website}
                      onChange={handleFormChange}
                      placeholder="https://www.exemplo.org.br"
                      variant="outlined"
                    />
                  </Grid>

                  {/* CHAVE PIX - Apenas para ONGs */}
                  {organizationType === "ong" && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Chave PIX"
                        name="pix"
                        value={formData.pix}
                        onChange={handleFormChange}
                        placeholder="E-mail, telefone, CPF/CNPJ ou chave aleatória"
                        variant="outlined"
                        helperText="Chave PIX para receber doações"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={4}>
                    <URLInput
                      fullWidth
                      label="Instagram"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleFormChange}
                      placeholder="https://www.instagram.com/usuario"
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <URLInput
                      fullWidth
                      label="Facebook"
                      name="facebook"
                      value={formData.facebook}
                      onChange={handleFormChange}
                      placeholder="https://www.facebook.com/pagina"
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <URLInput
                      fullWidth
                      label="X / Twitter"
                      name="x"
                      value={formData.x}
                      onChange={handleFormChange}
                      placeholder="https://x.com/usuario"
                      variant="outlined"
                    />
                  </Grid>

                  {/* SELEÇÃO DE CAUSAS */}
                  <Grid item xs={12}>
                    <Typography 
                      variant="h6" 
                      gutterBottom 
                      sx={{ 
                        mt: 2, 
                        color: formErrors.causes ? 'error.main' : theme.palette.primary.dark,
                        fontWeight: 600
                      }}
                    >
                      Áreas de Atuação *
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined" error={formErrors.causes}>
                      <InputLabel id="causes-label">Selecione as causas *</InputLabel>
                      <Select
                        labelId="causes-label"
                        multiple
                        value={selectedCauses}
                        onChange={handleCausesChange}
                        input={<OutlinedInput label="Selecione as causas *" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const cause = causes.find(c => c.id === value);
                              return (
                                <Chip 
                                  key={value} 
                                  label={cause?.name || value} 
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {causes.map((cause) => (
                          <MenuItem key={cause.id} value={cause.id}>
                            <Checkbox checked={selectedCauses.indexOf(cause.id) > -1} />
                            {cause.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.causes && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          Selecione pelo menos uma causa
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography 
                      variant="h6" 
                      gutterBottom 
                      sx={{ 
                        mt: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                      }}
                    >
                      Imagens da Organização
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography 
                      variant="subtitle1" 
                      gutterBottom 
                      sx={{ fontWeight: 500 }}
                    >
                      Logo da Organização
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Formatos aceitos: PNG, JPEG, JPG (máx. 5MB)
                    </Typography>
                    <Box>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ 
                          mb: 2,
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.light,
                            color: 'white',
                            borderColor: theme.palette.primary.light,
                          }
                        }}
                      >
                        Carregar logo
                        <input 
                          type="file" 
                          hidden 
                          accept=".png,.jpeg,.jpg,.PNG,.JPEG,.JPG" 
                          onChange={handleLogoUpload} 
                        />
                      </Button>
                      {logoFile && (
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            p: 1, 
                            border: '1px solid #eee',
                            borderRadius: 1,
                            bgcolor: '#f8f8f8',
                            mt: 1
                          }}
                        >
                          <Typography variant="body2">{logoFile.name}</Typography>
                          <Typography variant="caption" sx={{ mx: 1, color: 'text.secondary' }}>
                            {Math.round(logoFile.size / 1024)} KB
                          </Typography>
                          <IconButton size="small" onClick={handleRemoveLogo}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography 
                      variant="subtitle1" 
                      gutterBottom 
                      sx={{ fontWeight: 500 }}
                    >
                      Foto de Capa
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Formatos aceitos: PNG, JPEG, JPG (máx. 5MB)
                    </Typography>
                    <Box>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ 
                          mb: 2,
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.light,
                            color: 'white',
                            borderColor: theme.palette.primary.light,
                          }
                        }}
                      >
                        Carregar capa
                        <input 
                          type="file" 
                          hidden 
                          accept=".png,.jpeg,.jpg,.PNG,.JPEG,.JPG" 
                          onChange={handleCoverUpload} 
                        />
                      </Button>
                      {coverFile && (
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            p: 1, 
                            border: '1px solid #eee',
                            borderRadius: 1,
                            bgcolor: '#f8f8f8',
                            mt: 1
                          }}
                        >
                          <Typography variant="body2">{coverFile.name}</Typography>
                          <Typography variant="caption" sx={{ mx: 1, color: 'text.secondary' }}>
                            {Math.round(coverFile.size / 1024)} KB
                          </Typography>
                          <IconButton size="small" onClick={handleRemoveCover}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* GALERIA DE IMAGENS */}
                  <Grid item xs={12}>
                    <Typography 
                      variant="subtitle1" 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 500,
                        color: formErrors.galleryImages ? 'error.main' : 'inherit'
                      }}
                    >
                      Galeria de Imagens ({galleryFiles.length}/5) *
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Adicione no mínimo 4 fotos que representem as atividades da organização
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Formatos aceitos: PNG, JPEG, JPG (máx. 5MB por arquivo)
                    </Typography>
                    
                    <Box>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<PhotoCameraIcon />}
                        disabled={galleryFiles.length >= 5}
                        sx={{ 
                          mb: 2,
                          color: formErrors.galleryImages ? 'error.main' : theme.palette.primary.main,
                          borderColor: formErrors.galleryImages ? 'error.main' : theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: formErrors.galleryImages ? 'error.light' : theme.palette.primary.light,
                            color: 'white',
                            borderColor: formErrors.galleryImages ? 'error.light' : theme.palette.primary.light,
                          }
                        }}
                      >
                        Adicionar fotos
                        <input 
                          type="file" 
                          hidden 
                          accept=".png,.jpeg,.jpg,.PNG,.JPEG,.JPG" 
                          multiple
                          onChange={handleGalleryUpload} 
                        />
                      </Button>
                      
                      {galleryFiles.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {galleryFiles.map((file, index) => (
                            <Box 
                              key={index}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                p: 1, 
                                border: '1px solid #eee',
                                borderRadius: 1,
                                bgcolor: '#f8f8f8'
                              }}
                            >
                              <Typography variant="body2">{file.name}</Typography>
                              <Typography variant="caption" sx={{ mx: 1, color: 'text.secondary' }}>
                                {Math.round(file.size / 1024)} KB
                              </Typography>
                              <IconButton size="small" onClick={() => handleRemoveGalleryImage(index)}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {formErrors.galleryImages && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          Adicione pelo menos 4 fotos para a galeria
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography 
                      variant="h6" 
                      gutterBottom 
                      sx={{ 
                        mt: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                      }}
                    >
                      ODS da ONU
                    </Typography>
                    <Grid container spacing={isExtraSmall ? 1 : 2}>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map((number) => (
                        <Grid item key={number} xs={4} sm={3} md={2} lg={1.5}>
                          <Box 
                            sx={{ 
                              position: 'relative', 
                              textAlign: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleOdsSelection(number)}
                          >
                            <Checkbox
                              id={`ods-${number}`}
                              checked={selectedOds.includes(number)}
                              onChange={() => handleOdsSelection(number)}
                              sx={{ 
                                position: 'absolute', 
                                top: -10, 
                                right: -10, 
                                zIndex: 1,
                                color: theme.palette.primary.main,
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                },
                                padding: isExtraSmall ? '2px' : '8px'
                              }}
                            />
                            <Box 
                              component="img" 
                              src={`/images/ods-${number < 10 ? '0' + number : number}.png`}
                              alt={`ODS ${number}`}
                              sx={{ 
                                width: '100%', 
                                opacity: selectedOds.includes(number) ? 1 : 0.6,
                                border: selectedOds.includes(number) ? `2px solid ${theme.palette.primary.main}` : 'none',
                                borderRadius: 1,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  opacity: 0.9
                                }
                              }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 3 }} />
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: { xs: '1.2rem', sm: '1.5rem' }
                      }}
                    >
                      Dados para Login na Plataforma
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nome"
                      name="name"
                      value={loginData.name}
                      onChange={handleLoginDataChange}
                      placeholder="Seu nome completo"
                      variant="outlined"
                      required
                      error={loginErrors.name}
                      helperText={loginErrors.name ? "Nome é obrigatório" : ""}
                      disabled={isEditMode}
                      InputProps={{
                        sx: isEditMode ? {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        } : {}
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                                          <TextField
                        fullWidth
                        label="E-mail"
                        name="email"
                        type="email"
                        value={loginData.email}
                        onChange={handleLoginDataChange}
                        placeholder="seu.email@exemplo.com"
                        variant="outlined"
                        required
                        error={!!loginErrors.email}
                        helperText={loginErrors.email || ""}
                        disabled={isEditMode}
                        InputProps={{
                          sx: isEditMode ? {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          } : {}
                        }}
                      />
                  </Grid>
                  
                  {!isEditMode && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Senha"
                          name="password"
                          type="password"
                          value={loginData.password}
                          onChange={handleLoginDataChange}
                          placeholder="Senha"
                          variant="outlined"
                          required
                          error={loginErrors.password}
                          helperText={loginErrors.password ? "Senha é obrigatória" : ""}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Confirme a Senha"
                          name="confirmPassword"
                          type="password"
                          value={loginData.confirmPassword}
                          onChange={handleLoginDataChange}
                          placeholder="Confirme sua senha"
                          variant="outlined"
                          required
                          error={loginErrors.confirmPassword}
                          helperText={loginErrors.confirmPassword ? "As senhas não conferem" : ""}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Box sx={{ 
                  mt: { xs: 3, sm: 4 }, 
                  textAlign: 'center',
                  width: '100%'
                }}>
                  {errorMessage && (
                    <Alert 
                      severity={errorType}
                      sx={{ mb: 2, textAlign: 'left' }}
                      onClose={() => clearError()}
                    >
                      <Typography variant="body2">
                        {errorMessage}
                      </Typography>
                      {errorType === 'warning' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {/* Só mostra mensagem sobre campos vermelhos se realmente há campos com erro */}
                          {(Object.values(formErrors).some(error => error) || Object.values(loginErrors).some(error => error))
                            ? "Verifique os campos marcados em vermelho e tente novamente."
                            : "Corrija o problema informado e tente novamente."
                          }
                        </Typography>
                      )}
                    </Alert>
                  )}
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{ 
                      py: { xs: 1.25, sm: 1.5 },
                      px: { xs: 3, sm: 4 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 600,
                      borderRadius: { xs: 1.5, sm: 2 },
                      background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)',
                      transition: 'all 0.3s ease',
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #4F97FF 0%, #294BB6 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(41, 75, 182, 0.25)'
                      },
                      '&:active': {
                        transform: 'translateY(0)'
                      }
                    }}
                  >
                    {loading ? "Enviando..." : (isEditMode ? "Atualizar Cadastro" : "Cadastrar")}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Container>
        )}
      </Box>
    </>
  );
}
